import React, { useContext, useState, useCallback, useMemo, useRef } from "react";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import SessionPlanDisplay from "../plan/SessionPlanDisplay";
import SaveSessionDialog from "./SaveSessionDialog";
import SessionActionsSheet from "./SessionActionsSheet";
import ShareLinkDialog from "../share/ShareLinkDialog";
import { formatDuration } from "../../utils/time";
import useTotalDuration from "../../hooks/useTotalDuration";
import {
  formatCost,
  formatTokens,
  formatUsageSummary,
} from "../../apis/llm/usage";
import {
  Play,
  MoreHorizontal,
  Clock,
  CheckCircle,
  Sparkles,
  Pencil,
  Receipt,
  Shuffle,
} from "lucide-react";

const ActiveState = ({ onOpenGenerate }) => {
  const {
    workoutPlan,
    loadSessionIntoCoach,
    clearSession,
    // Renaming the session calls this directly with an empty path (issue #44);
    // the row handlers ride along in planDisplayProps.
    updatePlanNode,
    planDisplayProps,
    llmSettings,
    userProfile,
    lastGenerationUsage,
    money,
  } = useContext(WorkoutContext);

  // Show the "guided by recent history" notice from the persisted plan meta, so
  // it survives a page reload (issue #26). It is stamped onto the plan only at
  // generation time and stripped when a saved session is loaded, so it never
  // lingers on a subsequently loaded session (issue #9).
  const guidedNotice = workoutPlan?.meta?.guidance?.guided
    ? workoutPlan.meta.guidance
    : null;

  // What "Mixed" actually resolved to for this session. Stamped on the plan at
  // generation time alongside the guidance meta, so it survives a reload the
  // same way. Without it the rotation is invisible: a session that genuinely
  // varied looks identical to one that did not, which is how Mixed earned its
  // reputation for always producing the same thing.
  const mixedNotice = workoutPlan?.meta?.mixedArchetype?.name
    ? workoutPlan.meta.mixedArchetype
    : null;

  // What the current plan cost to generate (issue #71). Deliberately matched on
  // plan id and held in memory only — it belongs to the generation, not to the
  // plan, so loading a saved session or reloading the page clears it rather
  // than attributing a stale cost to a session that did not incur it.
  const generationCost =
    lastGenerationUsage &&
    workoutPlan?.id &&
    lastGenerationUsage.planId === workoutPlan.id
      ? lastGenerationUsage
      : null;
  const generationCostLabel = generationCost
    ? formatUsageSummary(generationCost, money)
    : null;

  const [saveOpen, setSaveOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Inline session-title rename (issue #44). Commits on Enter/blur via a
  // root-level updatePlanNode; Escape cancels. Empty input keeps the old name.
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const titleCancelledRef = useRef(false);

  const startEditTitle = useCallback(() => {
    titleCancelledRef.current = false;
    setTitleDraft(workoutPlan?.label || "");
    setEditingTitle(true);
  }, [workoutPlan]);

  const commitTitle = useCallback(() => {
    setEditingTitle(false);
    if (titleCancelledRef.current) return;
    const next = titleDraft.trim();
    if (next && next !== workoutPlan?.label) {
      updatePlanNode([], { label: next });
    }
  }, [titleDraft, workoutPlan, updatePlanNode]);

  // Check if this plan was recently completed
  const completedDate = useMemo(() => {
    try {
      const stored = JSON.parse(
        localStorage.getItem("completedSessions") || "[]",
      );
      const match = stored.find(
        (s) =>
          s.plan_data?.label &&
          workoutPlan?.label &&
          s.plan_data.label === workoutPlan.label,
      );
      if (match?.ended_at) {
        return new Date(match.ended_at).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        });
      }
    } catch {
      /* ignore */
    }
    return null;
  }, [workoutPlan]);

  const totalSeconds = useTotalDuration(workoutPlan);
  const formattedDuration = formatDuration(totalSeconds);
  const projectedEnd =
    totalSeconds > 0
      ? new Date(Date.now() + totalSeconds * 1000).toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        })
      : null;

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {/* Session Header */}
      <Card className="bg-surface/60 backdrop-blur border border-border">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {editingTitle ? (
                <Input
                  autoFocus
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={commitTitle}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.currentTarget.blur();
                    } else if (e.key === "Escape") {
                      titleCancelledRef.current = true;
                      e.currentTarget.blur();
                    }
                  }}
                  aria-label="Session title"
                  className="h-auto py-0.5 px-1 -ml-1 text-xl font-semibold border-0 bg-transparent shadow-none focus-visible:ring-1 focus-visible:ring-primary/40 focus-visible:ring-offset-0"
                />
              ) : (
                <button
                  type="button"
                  onClick={startEditTitle}
                  aria-label="Rename session"
                  title="Rename session"
                  className="group/title flex items-center gap-2 min-w-0 max-w-full text-left"
                >
                  <CardTitle className="text-xl truncate">
                    {workoutPlan?.label || "Your session"}
                  </CardTitle>
                  <Pencil className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60 group-hover/title:text-foreground transition-colors" />
                </button>
              )}
              {workoutPlan?.description && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                  {workoutPlan.description}
                </p>
              )}
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
                {completedDate && (
                  <span className="flex items-center gap-1.5 text-green-400">
                    <CheckCircle className="h-4 w-4" />
                    Completed {completedDate}
                  </span>
                )}
                {formattedDuration && (
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-4 w-4" />
                    {formattedDuration}
                  </span>
                )}
                {projectedEnd && <span>Ends ~{projectedEnd}</span>}
                {mixedNotice && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary transition-colors hover:bg-primary/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                      >
                        <Shuffle className="h-3.5 w-3.5 shrink-0" />
                        {mixedNotice.mode === "next"
                          ? "What's next"
                          : "Surprise me"}
                        : {mixedNotice.name}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-72">
                      <div className="flex items-start gap-2">
                        <Shuffle className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                        <div className="space-y-1.5">
                          <p className="text-sm font-medium text-foreground">
                            {mixedNotice.mode === "next"
                              ? "Why this session"
                              : "What Surprise me picked"}
                          </p>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {mixedNotice.mode === "next"
                              ? mixedNotice.reason ||
                                "Picked to balance your week of training."
                              : `Surprise me rotates through different session shapes rather than repeating one formula. This time it built ${mixedNotice.name.replace(/^plus /, "a session with ")}.`}
                          </p>
                          {mixedNotice.styles?.length > 0 && (
                            <p className="text-xs text-muted-foreground/80">
                              Styles: {mixedNotice.styles.join(", ")}.
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground/80">
                            Regenerate keeps this shape; generating fresh picks
                            a new one.
                          </p>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
                {guidedNotice && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary transition-colors hover:bg-primary/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                      >
                        <Sparkles className="h-3.5 w-3.5 shrink-0" />
                        History-guided
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-72">
                      <div className="flex items-start gap-2">
                        <Sparkles className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                        <div className="space-y-1.5">
                          <p className="text-sm font-medium text-foreground">
                            Recent-history guidance
                          </p>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {guidedNotice.rationale ||
                              "This session was generated with awareness of your recent training to vary the stimulus and avoid overtraining."}
                          </p>
                          <p className="text-xs text-muted-foreground/80">
                            Based on your last {guidedNotice.lookbackDays} days
                            {guidedNotice.sessionsConsidered > 0 &&
                              ` (${guidedNotice.sessionsConsidered} ${
                                guidedNotice.sessionsConsidered === 1
                                  ? "session"
                                  : "sessions"
                              })`}
                            .
                          </p>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
                {generationCostLabel && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                      >
                        <Receipt className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{generationCostLabel}</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-72">
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-foreground">
                          Generation cost
                        </p>
                        <dl className="space-y-1 text-xs text-muted-foreground">
                          <div className="flex justify-between gap-4">
                            <dt>Model</dt>
                            <dd className="font-mono text-right truncate">
                              {generationCost.model}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-4">
                            <dt>Input tokens</dt>
                            <dd className="tabular-nums">
                              {formatTokens(generationCost.inputTokens)}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-4">
                            <dt>Output tokens</dt>
                            <dd className="tabular-nums">
                              {formatTokens(generationCost.outputTokens)}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-4">
                            <dt>Provider calls</dt>
                            <dd className="tabular-nums">
                              {generationCost.calls}
                            </dd>
                          </div>
                          {generationCost.cost !== null && (
                            <div className="flex justify-between gap-4 pt-1 border-t border-border/50 text-foreground">
                              <dt>Total</dt>
                              <dd className="tabular-nums font-medium">
                                {formatCost(generationCost.cost, money)}
                              </dd>
                            </div>
                          )}
                        </dl>
                        <p className="text-xs text-muted-foreground/80 leading-relaxed">
                          {generationCost.reporting === "free"
                            ? "Generated locally through Ollama, so it cost nothing."
                            : generationCost.isActual
                              ? "Exact cost reported by OpenRouter for this generation."
                              : "Estimated from token counts and published rates — this provider does not report actual spend. Switch to OpenRouter for exact figures."}
                        </p>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
            {/* Save, regenerate, share, copy and clear live in one sheet
                (#111): as a row of buttons they wrapped onto two lines at
                phone width and outshouted Send to Coach. */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setActionsOpen(true)}
              aria-label="Session actions"
              title="Session actions"
              className="-mr-2 -mt-1 shrink-0 text-muted-foreground hover:text-foreground"
            >
              <MoreHorizontal className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Plan Display — tap any row to view/edit/AI-enhance that step. */}
      <Card className="bg-surface/60 backdrop-blur border border-border">
        <CardContent className="pt-6">
          <SessionPlanDisplay
            plan={workoutPlan}
            editable
            showTotal={false}
            {...planDisplayProps}
            llmSettings={llmSettings}
            userProfile={userProfile}
          />
        </CardContent>
      </Card>

      {/* Primary CTA: Send to Coach */}
      <div className="sticky bottom-16 md:bottom-4 z-40 pb-2">
        <Button
          onClick={() => loadSessionIntoCoach()}
          className="w-full py-6 text-lg font-bold shadow-lg shadow-primary/20"
          size="lg"
        >
          <Play className="mr-2 h-5 w-5" />
          Send to Coach
        </Button>
      </div>

      {/* Dialogs */}
      <SessionActionsSheet
        open={actionsOpen}
        onOpenChange={setActionsOpen}
        plan={workoutPlan}
        onSave={() => setSaveOpen(true)}
        onRegenerate={onOpenGenerate}
        onShare={() => setShareOpen(true)}
        onClear={clearSession}
      />
      <SaveSessionDialog open={saveOpen} onOpenChange={setSaveOpen} />
      <ShareLinkDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        request={{ kind: "plan", plan: workoutPlan }}
        defaultTitle={workoutPlan?.label || ""}
      />
    </div>
  );
};

export default ActiveState;
